import {
  GuestMemberInfo,
  GuestPortalMemberInfo,
} from './GuestPortalMemberInfo';
import {
  Coverage,
  CoverageEffDateRange,
  Eligibility,
  MemberCoverage,
  TypeCode,
} from './PortalMemberInfo';

const coverageEffDateRange: CoverageEffDateRange = {
  startDate: '',
  endDate: '2099-12-31',
};

const typeCode: TypeCode = {
  desc: 'MEDICAL',
  coverageTypeCode: 'M',
};

const coverageType: Coverage = {
  coverageEffDateRange,
  typeCode,
  reciprocityId: '52',
  situsState: '',
};

const memberHealthCoverage: MemberCoverage = {
  coverageType: [coverageType],
};

const eligibility: Eligibility = {
  memberHealthCoverage,
};

const guestMemberInfo: GuestMemberInfo = {
  eligibility: [eligibility],
  lineOfBusiness: 'E&I',
};

export const mockMedicalGuestPortalMemberInfo: GuestPortalMemberInfo = {
  memberInfo: [guestMemberInfo],
};

export const MockCnSPlans = {
  medical: {
    memberInfo: [
      {
        eligibility: [
          {
            memberHealthCoverage: {
              coverageType: [
                {
                  situsState: 'AZ',
                  coverageEffDateRange,
                  typeCode: {
                    desc: 'MEDICAL',
                    coverageTypeCode: 'M',
                    reciprocityId: 'AZCAID', // Should have providers in Arizona
                  },
                },
              ],
            },
          },
        ],
        lineOfBusiness: 'C&S',
        population: 'Medicaid',
        reciprocityId: 'AZCAID',
      },
    ],
  },
  behavioral: {
    memberInfo: [
      {
        eligibility: [
          {
            memberHealthCoverage: {
              coverageType: [
                {
                  coverageEffDateRange,
                  typeCode: {
                    desc: 'BEHAVIORAL HEALTH',
                    coverageTypeCode: 'B',
                    reciprocityId: '321', // Should have providers in Arizona
                  },
                },
              ],
            },
          },
        ],
        lineOfBusiness: 'C&S',
        population: 'Medicaid',
      },
    ],
  },
  dental: {
    memberInfo: [
      {
        eligibility: [
          {
            memberHealthCoverage: {
              coverageType: [
                {
                  coverageEffDateRange,
                  typeCode: {
                    desc: 'DENTAL',
                    coverageTypeCode: 'D',
                    reciprocityId: 'FFS000000017', // Should have providers in Arizona
                  },
                },
              ],
            },
          },
        ],
        lineOfBusiness: 'C&S',
        population: 'Medicaid',
      },
    ],
  },
};

export const MockMnRPlans = {
  medicalCosmos: {
    memberInfo: [
      {
        eligibility: [
          {
            memberHealthCoverage: {
              coverageType: [
                {
                  coverageEffDateRange,
                  typeCode: {
                    desc: 'MEDICAL',
                    coverageTypeCode: 'M',
                    reciprocityId: '124,NatAncMedicare', // Should have providers in Phoenix, Arizona
                  },
                },
              ],
            },
          },
        ],
        lineOfBusiness: 'M&R',
        population: 'COSMOS',
        reciprocityId: '124,NatAncMedicare',
      },
    ],
  },
  medicalDsnp: {
    memberInfo: [
      {
        eligibility: [
          {
            memberHealthCoverage: {
              coverageType: [
                {
                  coverageEffDateRange,
                  typeCode: {
                    desc: 'MEDICAL',
                    coverageTypeCode: 'M',
                    reciprocityId: '321,124CR', // Should have providers in Phoenix, Arizona,
                  },
                  snpType: 'DSNP',
                },
              ],
            },
          },
        ],
        lineOfBusiness: 'M&R',
        population: 'COSMOS',
        reciprocityId: '321,124CR',
      },
    ],
  },
  behavioral: {
    memberInfo: [
      {
        eligibility: [
          {
            memberHealthCoverage: {
              coverageType: [
                {
                  coverageEffDateRange,
                  typeCode: {
                    desc: 'BEHAVIORAL HEALTH',
                    coverageTypeCode: 'B',
                    reciprocityId: 'MEDICARE', // Should have providers in Phoenix, Arizona,
                  },
                },
              ],
            },
          },
        ],
        lineOfBusiness: 'M&R',
        population: 'COSMOS',
      },
    ],
  },
  dental: {
    memberInfo: [
      {
        eligibility: [
          {
            memberHealthCoverage: {
              coverageType: [
                {
                  coverageEffDateRange,
                  typeCode: {
                    desc: 'DENTAL',
                    coverageTypeCode: 'D',
                    reciprocityId: 'FFS000000026', // Should have providers in Phoenix, Arizona,
                  },
                },
              ],
            },
          },
        ],
        lineOfBusiness: 'M&R',
        population: 'COSMOS',
      },
    ],
  },
  vision: {
    memberInfo: [
      {
        eligibility: [
          {
            memberHealthCoverage: {
              coverageType: [
                {
                  coverageEffDateRange,
                  typeCode: {
                    desc: 'VISON',
                    coverageTypeCode: 'V',
                    reciprocityId: '2', // Should have providers in Phoenix, Arizona,
                  },
                },
              ],
            },
          },
        ],
        lineOfBusiness: 'M&R',
        population: 'COSMOS',
      },
    ],
  },
};

// default membershipCategory to empty string
export const getGuestPortalMemberInfo: ({
  lob,
  coverageType,
  population,
  membershipCategory, // Declare membershipCategory here
  reciprocityId,
  situsState,
  planCategory,
  guestPlanIdentifier,
}: {
  lob: string;
  coverageType: string; // Ensure this is used as a type, not a value
  population: string;
  membershipCategory?: string; // Declare membershipCategory as optional in the type definition
  reciprocityId: string;
  situsState: string;
  planCategory?: string;
  guestPlanIdentifier?: string;
}) => GuestPortalMemberInfo = ({
  lob,
  coverageType,
  population,
  membershipCategory = '', // Set default value to an empty string
  reciprocityId,
  situsState,
  planCategory,
  guestPlanIdentifier,
}) => {
  const lobMapping: { [key: string]: string } = {
    eni: 'E&I',
    cns: 'C&S',
    mnr: 'M&R',
    ifp: 'IFP',
  };
  const LineOfBusiness = lobMapping[lob.toLowerCase()];
  // default memebershipCategory to Public_Exchange if lob is IFP and population is IFP
  if (
    lob.toUpperCase() === 'IFP' &&
    population === 'IFP' &&
    (!membershipCategory || membershipCategory === '')
  ) {
    membershipCategory = 'PUBLIC EXCHANGE';
  }
  const guestMemberInfo: GuestPortalMemberInfo = {
    memberInfo: [
      {
        eligibility: [eligibility],
        lineOfBusiness: LineOfBusiness,
        reciprocityId,
        population,
        membershipCategory,
        planCategory,
        guestPlanIdentifier,
      },
    ],
  };
  guestMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].reciprocityId =
    reciprocityId;
  guestMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].situsState =
    situsState;
  if (coverageType === 'D') {
    guestMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].typeCode =
      {
        desc: 'DENTAL',
        coverageTypeCode: 'D',
        reciprocityId,
      };
  } else if (coverageType === 'B') {
    guestMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].typeCode =
      {
        desc: 'BEHAVIORAL HEALTH',
        coverageTypeCode: 'B',
        reciprocityId,
      };
  } else if (coverageType === 'V') {
    guestMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].typeCode =
      {
        desc: 'VISION',
        coverageTypeCode: 'V',
        reciprocityId,
      };
  } else {
    guestMemberInfo.memberInfo[0].eligibility[0].memberHealthCoverage.coverageType[0].typeCode =
      {
        desc: 'MEDICAL',
        coverageTypeCode: 'M',
        reciprocityId,
      };
  }
  return guestMemberInfo;
};

export const defaultPopulationForLob: { [key: string]: string } = {
  ENI: 'UNET',
  CNS: 'Medicaid',
  MNR: 'COSMOS',
  IFP: 'IFP',
};

export const reciprocityIdMapping = {
  CNS: {
    Medicaid: {
      M: ['AZCAID', 'NYACA', 'TXCHIP', 'TXSTAR', 'TXMMP'],
      B: ['321'],
      D: ['FFS000000075'],
      V: ['1'],
    },
    DSNP: {
      M: ['203,124CR'],
      B: ['203'],
      V: ['203,124CR'],
      D: [`FFS000000075`],
    },
  },
  ENI: {
    UNET: {
      M: ['52', '51'],
      D: ['PPO000000001', 'PPO000000006'],
      B: ['Employer Guest'],
      V: [`1`, '7'],
    },
    USP: {
      OXFORD: {
        M: ['LIBERTY_CORE', 'METRO'],
        D: ['PPO000000001', 'PPO000000006'],
        B: ['Employer Guest'],
        V: ['1'],
      },
      NON_OXFORD: {
        // note: using NON_OXFORD (underscore) as key for easier lookup
        M: ['52'],
        D: ['PPO000000001', 'PPO000000006'],
        B: ['Employer Guest'],
        V: ['1'],
      },
    },
  },
  IFP: {
    IFP: {
      'PUBLIC EXCHANGE': {
        M: ['916'],
        D: ['PPO000000016'],
        B: ['916'],
        V: ['1'],
      },
    },
  },
  MNR: {
    COSMOS: {
      M: ['124,NatAncMedicare'],
      D: ['FS000000026'],
      B: ['MEDICARE'],
      V: ['2'],
    },
    DSNP: {
      M: ['321,124CR'],
      D: ['FS000000026'],
      B: ['MEDICARE'],
      V: ['2'],
    },
  },
};

export const getReciprocityIds = ({
  lob,
  coverageType = 'M',
  population = '',
  membershipCategory = '', // Declare membershipCategory as optional in the type definition
}: {
  lob: string;
  coverageType?: string;
  population?: string;
  membershipCategory?: string; // Declare membershipCategory as optional in the type definition
}): string[] => {
  const lobKey = lob.toUpperCase();
  const populationKey = (population || defaultPopulationForLob[lobKey]).replace(
    / /g,
    '_'
  ); // Replace spaces with underscores for key matching
  let membershipCategoryKey = membershipCategory || null;

  if (
    lobKey === 'ENI' &&
    populationKey === 'USP' &&
    (!membershipCategoryKey || membershipCategoryKey === '')
  ) {
    membershipCategoryKey = 'NON_OXFORD'; // Default to NON_OXFORD if membershipCategory is not provided
  }

  if (
    lobKey === 'IFP' &&
    populationKey === 'IFP' &&
    (!membershipCategoryKey || membershipCategoryKey === '')
  ) {
    membershipCategoryKey = 'PUBLIC EXCHANGE'; // Default to PUBLIC_EXCHANGE if membershipCategory is not provided
  }

  try {
    if (membershipCategoryKey && membershipCategoryKey !== '') {
      return (
        reciprocityIdMapping[lobKey][populationKey][membershipCategoryKey][
          coverageType
        ] || []
      );
    } else {
      return reciprocityIdMapping[lobKey][populationKey][coverageType] || [];
    }
  } catch {
    return [];
  }
};

// Note: The values used in the reciprocityIdMapping should correspond to actual reciprocity IDs used in your system.
